// qchat_fe/src/Component/SideData/Overview/AiAgentWorkFlow/TriggerFieldMappingModal.jsx
import React, { useMemo, useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { FaTimes, FaArrowRight, FaExclamationTriangle } from "react-icons/fa";
import { canonicalTypeOf, conversionFor, prettyCanonicalType } from "./triggerEligibility";
import { TRIGGER_CONVERT_TO_ALLOWED } from "./triggerAttachment";

const CONVERT_TO_LABELS = {
  string: "String", number: "Number", integer: "Integer", boolean: "Boolean",
  array: "List of strings", json: "Object (parse JSON)", json_array: "List of objects",
  date: "Date", time: "Time", datetime: "Date & Time",
};

export default function TriggerFieldMappingModal({ isOpen, onClose, definition, classification, siteFields, onAttach, initialAttachment = null }) {
  const [rows, setRows] = useState([]);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!isOpen) return;
    setError("");
    
    const seed = [];
    const resultByField = Object.fromEntries(((classification && classification.fieldResults) || []).map((r) => [r.field, r]));
    const initialOverrides = (initialAttachment && initialAttachment.field_overrides) || {};

    const allKeys = new Set([
      ...Object.keys(resultByField),
      ...Object.keys(initialOverrides)
    ]);

    for (const fieldKey of allKeys) {
      const overrideEntry = initialOverrides[fieldKey];
      const autoResult = resultByField[fieldKey];
      
      let source = "";
      let convert_to = null;
      let mode = "pick";

      if (overrideEntry && typeof overrideEntry.source === "string") {
        source = overrideEntry.source;
        convert_to = overrideEntry.convert_to || null;
        mode = source.match(/^\s*\{\{\s*([^{}]+?)\s*\}\}\s*$/) ? "pick" : "custom";
      } else if (autoResult && autoResult.source) {
        source = autoResult.source;
        convert_to = autoResult.convert_to || null;
      }

      seed.push({ 
        field: fieldKey, 
        source, 
        convert_to,
        mode, 
        isRequired: autoResult ? autoResult.isRequired : false, 
        targetType: autoResult ? autoResult.targetType : "string",
        candidates: (autoResult && autoResult.candidates) || siteFields || [], 
      });
    }
    
    setRows(seed);
  }, [isOpen, classification, initialAttachment, siteFields]);

  if (!isOpen || typeof document === "undefined") return null;

  const updateRow = (idx, patch) => setRows((prev) => prev.map((r, i) => (i === idx ? { ...r, ...patch } : r)));

  const convertOptionsFor = (row) => {
    const bare = row.source.match(/^\s*\{\{\s*([^{}]+?)\s*\}\}\s*$/);
    if (!bare) return [];
    const siteField = (siteFields || []).find((f) => f.key === bare[1]);
    if (!siteField) return [];
    
    const fromCanonical = canonicalTypeOf(siteField.type);
    const targetCanonical = canonicalTypeOf(row.targetType);
    
    if (fromCanonical === targetCanonical) return [];

    const opts = [];
    for (const conv of TRIGGER_CONVERT_TO_ALLOWED) {
      if (conv === "string") continue;
      const convTarget = conv === "array" ? "array<string>" 
                       : conv === "json" ? "object" 
                       : conv === "json_array" ? "array<object>" 
                       : conv;
      if (conversionFor(fromCanonical, convTarget) === conv) opts.push(conv);
    }
    return opts;
  };

  const handleSave = () => {
    for (const r of rows) {
      if (r.isRequired && !r.source.trim()) {
        setError(`Variable "${r.field}" is required and needs a mapping.`);
        return;
      }
      
      if (r.source.trim() && r.mode === "pick") {
        const bare = r.source.match(/^\s*\{\{\s*([^{}]+?)\s*\}\}\s*$/);
        if (bare) {
          const siteField = (siteFields || []).find((f) => f.key === bare[1]);
          if (siteField) {
            const fromCanonical = canonicalTypeOf(siteField.type);
            const targetCanonical = canonicalTypeOf(r.targetType);
            
            // ⭐ BLOCK if the types don't match AND no conversion option exists
            if (fromCanonical !== targetCanonical && !r.convert_to) {
               setError(`Type mismatch: "${bare[1]}" is ${siteField.type}, but "${r.field}" expects a ${prettyCanonicalType(r.targetType)}. Please update the form field type or choose a valid 'Convert to' option.`);
               return;
            }
          }
        }
      }
    }
    
    const overrides = {};
    for (const r of rows) {
      if (r.source.trim()) {
        overrides[r.field] = { source: r.source.trim() };
        if (r.convert_to) overrides[r.field].convert_to = r.convert_to;
      }
    }
    onAttach({ connector_definition_id: definition.definition_id || definition.operation_name, field_overrides: overrides });
  };

  return createPortal(
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }} onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} style={{ background: "white", borderRadius: 12, width: "100%", maxWidth: 800, maxHeight: "90vh", display: "flex", flexDirection: "column", boxShadow: "0 20px 60px rgba(0,0,0,0.3)" }}>
        
        <div style={{ display: "flex", justifyContent: "space-between", padding: "16px 20px", borderBottom: "1px solid #e2e8f0" }}>
          <div>
            <h3 style={{ margin: 0, fontSize: 16 }}>Map variables for "{definition.name}"</h3>
            <p style={{ margin: "4px 0 0", fontSize: 12, color: "#718096" }}>{definition.operation_name}</p>
          </div>
          <button onClick={onClose} style={{ background: "transparent", border: "none", fontSize: 20, cursor: "pointer", color: "#a0aec0" }}><FaTimes /></button>
        </div>

        <div style={{ padding: "16px 20px", overflowY: "auto", flex: 1 }}>
          {rows.length === 0 ? (
            <p style={{ fontSize: 13, color: "#718096" }}>No variables found to map.</p>
          ) : (
            <p style={{ fontSize: 13, color: "#4a5568", marginBottom: 16 }}>
              This action uses the following variables in its template. Please map a form field to each variable.
            </p>
          )}
          
          {rows.map((row, idx) => {
            const convertOpts = convertOptionsFor(row);
            return (
              <div key={row.field} style={{ border: "1px solid #e2e8f0", borderRadius: 8, padding: 14, marginBottom: 12 }}>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 32px 1fr", gap: 12, alignItems: "start" }}>
                  
                  <div>
                    <label style={{ fontSize: 11, color: "#718096", display: "block", marginBottom: 4 }}>Form Field</label>
                    {row.mode === "pick" ? (
                      <select
                        value={row.source}
                        onChange={(e) => updateRow(idx, { source: e.target.value })}
                        style={{ width: "100%", padding: "6px 8px", fontSize: 13, border: "1px solid #cbd5e0", borderRadius: 4 }}
                      >
                        <option value="">— pick a field —</option>
                        {row.candidates.map((c) => (
                          <option key={c.key} value={`{{ ${c.key} }}`}>{c.key} ({c.type})</option>
                        ))}
                      </select>
                    ) : (
                      <input
                        type="text"
                        value={row.source}
                        onChange={(e) => updateRow(idx, { source: e.target.value })}
                        style={{ width: "100%", padding: "6px 8px", fontSize: 13, border: "1px solid #cbd5e0", borderRadius: 4 }}
                      />
                    )}
                    <button
                      type="button"
                      onClick={() => updateRow(idx, { mode: row.mode === "pick" ? "custom" : "pick", source: "" })}
                      style={{ marginTop: 6, fontSize: 11, color: "#4299e1", background: "transparent", border: "none", cursor: "pointer", padding: 0 }}
                    >
                      {row.mode === "pick" ? "Use a custom template instead" : "Pick a field instead"}
                    </button>

                    {convertOpts.length > 0 && row.mode === "pick" && (
                      <div style={{ marginTop: 6 }}>
                        <label style={{ fontSize: 11, color: "#718096", display: "block", marginBottom: 2 }}>Convert to</label>
                        <select
                          value={row.convert_to || ""}
                          onChange={(e) => updateRow(idx, { convert_to: e.target.value || null })}
                          style={{ width: "100%", padding: "4px 6px", fontSize: 12, border: "1px solid #cbd5e0", borderRadius: 4 }}
                        >
                          <option value="">None</option>
                          {convertOpts.map((c) => <option key={c} value={c}>{CONVERT_TO_LABELS[c] || c}</option>)}
                        </select>
                      </div>
                    )}
                  </div>

                  <div style={{ textAlign: "center", paddingTop: 22 }}><FaArrowRight style={{ color: "#cbd5e0" }} /></div>

                  <div>
                    <label style={{ fontSize: 11, color: "#718096", display: "block", marginBottom: 4 }}>
                      Template Variable {row.isRequired ? <span style={{color: "#e53e3e"}}>(required)</span> : <span style={{color: "#a0aec0"}}>(optional)</span>}
                    </label>
                    <div style={{ padding: "6px 8px", fontSize: 13, fontFamily: "monospace", background: "#f7fafc", border: "1px solid #e2e8f0", borderRadius: 4 }}>
                      {`{{ ${row.field} }}`}
                    </div>
                    <div style={{ marginTop: 4, fontSize: 11, color: "#718096" }}>
                      expects <strong>{prettyCanonicalType(row.targetType)}</strong>
                    </div>
                  </div>

                </div>
              </div>
            );
          })}
          {error && <div style={{ color: "#c53030", fontSize: 12, marginTop: 8 }}><FaExclamationTriangle /> {error}</div>}
        </div>

        <div style={{ padding: "12px 20px", borderTop: "1px solid #e2e8f0", display: "flex", justifyContent: "flex-end", gap: 10 }}>
          <button onClick={onClose} style={{ padding: "8px 16px", background: "white", border: "1px solid #cbd5e0", borderRadius: 6, cursor: "pointer" }}>Cancel</button>
          <button onClick={handleSave} style={{ padding: "8px 16px", background: "#4299e1", color: "white", border: "none", borderRadius: 6, cursor: "pointer", fontWeight: 600 }}>Save Mapping</button>
        </div>
      </div>
    </div>,
    document.body
  );
}